import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { Container } from '../../styles/components.js';
import { AppImages } from '../../assets/index.js';

/* ─── Animations ─────────────────────────────────────────────────── */
const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const gridPulse = keyframes`
  0%, 100% { opacity: 0.15; }
  50% { opacity: 0.3; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(1deg); }
`;

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }
  })
};

/* ─── Styled Components ──────────────────────────────────────────── */
const HeroSection = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.background};
  padding: 120px 0 80px 0;
  
  /* Decorative Subtle Tech Grid Lines */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: 
      linear-gradient(rgba(99, 102, 241, 0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(99, 102, 241, 0.03) 1px, transparent 1px);
    background-size: 120px 120px;
    z-index: 0;
    pointer-events: none;
    animation: ${gridPulse} 8s ease-in-out infinite;
  }
`;

const HeroGrid = styled(Container)`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  align-items: center;
  gap: 4rem;
  position: relative;
  z-index: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 3rem;
  }
`;

const TextContent = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const GreetingBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary}0c;
  padding: 6px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  width: fit-content;
  border: 1px solid ${({ theme }) => theme.colors.primary}18;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.glowSm};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin: 0 auto;
  }
`;

const Name = styled(motion.h1)`
  font-size: clamp(2.8rem, 6vw, 4.8rem);
  line-height: 1.05;
  color: ${({ theme }) => theme.colors.textDark};
  font-weight: 900;
  margin-bottom: 0;
  letter-spacing: -0.04em;

  span.accent-text {
    background: ${({ theme }) => theme.colors.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    position: relative;
  }
`;

const TypewriterRow = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: clamp(1.2rem, 2.5vw, 1.8rem);
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textGray};
  min-height: 2.2rem;
  font-family: 'Space Grotesk', sans-serif;
  letter-spacing: -0.02em;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: center;
  }
`;

const TypedText = styled.span`
  color: ${({ theme }) => theme.colors.textDark};
`;

const Cursor = styled.span`
  display: inline-block;
  width: 3px;
  height: 1.1em;
  background: ${({ theme }) => theme.colors.primary};
  animation: ${blink} 1s step-end infinite;
  border-radius: 2px;
  flex-shrink: 0;
`;

const Description = styled(motion.p)`
  font-size: clamp(1rem, 1.6vw, 1.1rem);
  color: ${({ theme }) => theme.colors.textGray};
  max-width: 580px;
  line-height: 1.8;
  margin-bottom: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin: 0 auto;
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: 1.25rem;
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: center;
  }
`;

const PrimaryBtn = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 34px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-weight: 700;
  font-size: 0.9rem;
  font-family: 'Space Grotesk', sans-serif;
  cursor: pointer;
  text-decoration: none;
  background: ${({ theme }) => theme.colors.gradient};
  color: #fff;
  box-shadow: ${({ theme }) => theme.shadows.glow};
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  border: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.glowLg};
    filter: brightness(1.08);
  }
`;

const SecondaryBtn = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 32px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-weight: 700;
  font-size: 0.9rem;
  font-family: 'Space Grotesk', sans-serif;
  cursor: pointer;
  text-decoration: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textDark};
  border: 1.5px solid ${({ theme }) => theme.colors.border};
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary}08;
    transform: translateY(-2px);
  }
`;

const StatsStrip = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  width: 100%;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.card};
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}30;
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.glowSm};
  }
`;

const StatNumber = styled.span`
  font-size: 1.6rem;
  font-weight: 800;
  font-family: 'Space Grotesk', sans-serif;
  color: ${({ theme }) => theme.colors.textDark};
  line-height: 1;
`;

const StatLabel = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textGray};
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

/* Image side */
const ImageWrapper = styled(motion.div)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    order: -1;
  }
`;

/* Modern Tech Geometric Frame */
const PhotoFrame = styled.div`
  width: 380px;
  height: 420px;
  position: relative;
  z-index: 2;
  border-radius: 24px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.large};
  animation: ${float} 6s ease-in-out infinite;

  &::before {
    content: '';
    position: absolute;
    inset: -8px;
    border-radius: 32px;
    border: 1px dashed ${({ theme }) => theme.colors.primary}30;
    z-index: -1;
    pointer-events: none;
  }

  &::after {
    content: 'CODEX';
    position: absolute;
    bottom: -15px;
    right: 25px;
    font-family: 'Space Grotesk', monospace;
    font-size: 0.75rem;
    font-weight: 800;
    color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => theme.colors.background};
    padding: 2px 10px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 4px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.laptop}) {
    width: 310px;
    height: 350px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 250px;
    height: 290px;
  }
`;

const PhotoContent = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 14px;
  overflow: hidden;
  position: relative;
  background-color: ${({ theme }) =>
    theme.colors.background === '#030303' ? '#0d0d12' : '#f1f5f9'};
  border: 1px solid ${({ theme }) => theme.colors.border};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    filter: grayscale(10%) contrast(105%);
    transition: all 0.5s ease;

    &:hover {
      filter: grayscale(0%) contrast(100%);
      transform: scale(1.02);
    }
  }
`;

const FloatingLabel = styled(motion.div)`
  position: absolute;
  background: ${({ theme }) => theme.colors.glass};
  padding: 8px 16px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.large};
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 3;
  border: 1px solid ${({ theme }) => theme.colors.glassStroke};
  white-space: nowrap;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 6px #10b98180;
  }

  span {
    font-weight: 700;
    font-size: 0.8rem;
    color: ${({ theme }) => theme.colors.textDark};
    font-family: 'Space Grotesk', sans-serif;
  }
`;

/* ─── Typewriter hook ────────────────────────────────────────────── */
const ROLES = [
  'React Native Developer',
  'Mobile Engineer',
  'iOS & Android Expert',
  'Cross-Platform Specialist',
];

const useTypewriter = (words, speed = 80, pause = 1800) => {
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [reverse, setReverse] = useState(false);

  useEffect(() => {
    if (subIndex === words[index].length + 1 && !reverse) {
      const t = setTimeout(() => setReverse(true), pause);
      return () => clearTimeout(t);
    }
    if (subIndex === 0 && reverse) {
      setReverse(false);
      setIndex((i) => (i + 1) % words.length);
      return;
    }
    const t = setTimeout(() => {
      setSubIndex((v) => v + (reverse ? -1 : 1));
    }, reverse ? 40 : speed);
    return () => clearTimeout(t);
  }, [subIndex, index, reverse, words, speed, pause]);

  return words[index].substring(0, subIndex);
};

/* ─── Component ─────────────────────────────────────────────────── */
const Hero = () => {
  const typed = useTypewriter(ROLES);

  return (
    <HeroSection id="home">
      <HeroGrid>
        {/* ── Text ── */}
        <TextContent>
          <GreetingBadge custom={0} variants={fadeUp} initial="hidden" animate="visible">
            Hello, I'm Tauseef
          </GreetingBadge>

          <Name custom={1} variants={fadeUp} initial="hidden" animate="visible">
            Building{' '}
            <span style={{ display: 'block' }}>
              <span className="accent-text">Digital Products</span>
            </span>
            That Matter
          </Name>

          <TypewriterRow custom={2} variants={fadeUp} initial="hidden" animate="visible">
            <TypedText>{typed}</TypedText>
            <Cursor />
          </TypewriterRow>

          <Description custom={3} variants={fadeUp} initial="hidden" animate="visible">
            I'm a passionate React Native Developer crafting high-performance mobile applications
            and seamless user interfaces. With a focus on clean code and delightful UX,
            let's turn your ideas into reality.
          </Description>

          <ButtonGroup custom={4} variants={fadeUp} initial="hidden" animate="visible">
            <PrimaryBtn href="#projects">
              View My Work
            </PrimaryBtn>
            <SecondaryBtn href="#contact">
              Contact Me
            </SecondaryBtn>
          </ButtonGroup>

          <StatsStrip custom={5} variants={fadeUp} initial="hidden" animate="visible">
            <StatCard>
              <StatNumber>15+</StatNumber>
              <StatLabel>Apps Deployed</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>10+</StatNumber>
              <StatLabel>Happy Clients</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>3+</StatNumber>
              <StatLabel>Years Exp</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>5.0</StatNumber>
              <StatLabel>App Rating</StatLabel>
            </StatCard>
          </StatsStrip>
        </TextContent>

        {/* ── Image ── */}
        <ImageWrapper
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <PhotoFrame>
            <PhotoContent>
              <img src={AppImages.updatePortfolio} alt="Tauseef" />
            </PhotoContent>
          </PhotoFrame>

          <FloatingLabel
            style={{ top: '15%', right: '-5%' }}
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <span>Full Stack Developer</span>
          </FloatingLabel>
        </ImageWrapper>
      </HeroGrid>
    </HeroSection>
  );
};

export default Hero;